<template>
    <div class="min-h-screen bg-white">
        <div class="max-w-5xl mx-auto px-4 py-10">
            <h1 class="text-3xl font-marketing font-bold text-primary mb-2">My Library</h1>
            <p class="text-gray-600 font-sans mb-8">
                Your purchased e-books and audiobooks open right here in your browser — in the
                built-in reader and player — so you can read and listen on any device, with nothing
                extra to install and no file to download.
            </p>

            <Loader v-if="loading" />

            <div v-else-if="error" class="rounded-md border border-red-200 bg-red-50 p-4 text-red-700 font-sans">
                {{ error }}
            </div>

            <div v-else-if="purchases.length === 0"
                class="rounded-md border border-gray-200 bg-gray-50 p-8 text-center">
                <p class="text-gray-600 font-sans mb-4">You don't have any digital purchases yet.</p>
                <a href="/books"
                    class="inline-flex items-center px-5 py-2.5 rounded-md bg-accent text-white font-marketing font-semibold hover:bg-green-600 transition-colors">
                    Browse books
                </a>
            </div>

            <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div v-for="item in purchases" :key="item.id"
                    class="border border-gray-200 rounded-lg overflow-hidden flex flex-col">
                    <div class="aspect-[2/3] bg-gray-100 overflow-hidden">
                        <img v-if="item.cover_image" :src="item.cover_image" :alt="item.title"
                            class="w-full h-full object-cover" />
                    </div>
                    <div class="p-4 flex flex-col flex-1">
                        <h2 class="font-marketing font-semibold text-primary leading-tight mb-1">{{ item.title }}</h2>
                        <p class="text-sm text-gray-600 font-sans mb-1">{{ item.author }}</p>
                        <span v-if="item.is_deleted"
                            class="self-start mb-2 px-2.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-marketing font-semibold">
                            The publisher no longer offers this title — you still own it
                        </span>
                        <span v-else-if="item.is_unavailable"
                            class="self-start mb-2 px-2.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-marketing font-semibold">
                            No longer available to new buyers
                        </span>
                        <span v-else-if="item.is_test"
                            class="self-start mb-2 px-2.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-marketing font-semibold">
                            The publisher no longer lists this title — you still own it
                        </span>
                        <p class="text-xs uppercase tracking-wide text-gray-400 font-sans mb-4">
                            {{ item.format }} · {{ item.file_format }}
                        </p>
                        <a :href="openHref(item)"
                            class="mt-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-accent text-white font-marketing font-semibold hover:bg-green-600 transition-colors">
                            {{ item.format === 'audiobook' ? 'Listen' : 'Read' }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Loader from './Loader.vue'
import { userService } from '../services/user'

const purchases = ref([])
const loading = ref(true)
const error = ref('')

const openHref = (item) => {
    const base = item.format === 'audiobook' ? '/listen/' : '/read/'
    return `${base}${item.id}`
}

const load = async () => {
    loading.value = true
    error.value = ''
    const result = await userService.getDigitalPurchases()
    if (result.success) {
        purchases.value = Array.isArray(result.data) ? result.data : []
    } else {
        error.value = result.error || 'Failed to load your library'
    }
    loading.value = false
}

onMounted(load)
</script>
